import { memo, useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
import SnackBar from "../../../../../components/SnackBar";
import TableCardv2 from "../../../../../components/TableCardv2";
import { Calculate, RequestQuote } from "@mui/icons-material";
import { Autocomplete, Button, TextField, ToggleButton } from "@mui/material";
import GenericDatePicker from "../../../../../components/GenericDatePicker";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import GenericMenu from "../../../../../components/GenericMenu";
import styles from "./styles.module.css";
import { PreviaInssContext } from "../../../PreviaInssContext";
import { exportExcel } from "../../../../../utils/exportExcelFunction";
import {
  gerarRelatorioPreviaInssConsolidado,
  gerarRelatorioProjetoInssConsolidado,
} from "../../../../../services/api-inss.service";
import { IfPermissao, CHAVES_INSS_EXPORT } from "../../../../../config/Context/PermissoesContext";

const GerarRelatorioCompleto = memo(() => {
  const normalizarCategoriaMedidaJudicial = (valor) => {
    const txt = String(valor || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase();

    if (txt.includes("DESNECESS")) return "DESNECESSIDADE";
    if (txt.includes("SUGEST")) return "SUGESTAO";
    return "";
  };

  const inictialFilters = {
    calculoFilter: { calculo: "geral", sefip: false },
    verbas: "Completo",
    datas: { dtInicial: null, dtFinal: null },
    contas: [],
  };
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [loading, setLoading] = useState(false);
  const [dataTable, setDataTable] = useState({ data: [], dataFilter: [] });
  const [dataColumns, setDataColumns] = useState([]);
  const [filters, setFilters] = useState(inictialFilters);
  const [optionsContas, setOptionsContas] = useState([]);

  const { dados, handleBuscarResumoGeralVersao } = useContext(PreviaInssContext);

  // Flag para evitar múltiplas chamadas simultâneas
  const fetchingRef = useRef(false);
  
  // Ref para armazenar a função e evitar dependência no useCallback
  const handleBuscarResumoGeralVersaoRef = useRef(handleBuscarResumoGeralVersao);
  useEffect(() => {
    handleBuscarResumoGeralVersaoRef.current = handleBuscarResumoGeralVersao;
  }, [handleBuscarResumoGeralVersao]);

  // Função para buscar e atualizar os dados do relatório
  const fetchData = useCallback(async () => {
    if (!dados?.idEmpresa) return;

    // Evita chamada com dados ainda vazios (buscaDadosPrevia não terminou)
    const temDadosCarregados =
      (Array.isArray(dados?.verbasAjustadas) && dados.verbasAjustadas.length > 0) ||
      (Array.isArray(dados?.resumo) && dados.resumo.length > 0);
    if (!temDadosCarregados) return;

    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    try {
      setOptionsContas(dados.contasResumo || []);
      let resumoDaVersao = [];
      try {
        resumoDaVersao = (await handleBuscarResumoGeralVersaoRef.current()) || [];
      } catch (e) {
        console.warn("handleBuscarResumoGeralVersao falhou, usando fallback:", e);
      }
      const base = resumoDaVersao.length > 0 ? resumoDaVersao : (dados.resumo || []);
      setDataColumns(handleChangeColumns());
      setDataTable({ data: base, dataFilter: base });

      try {
        localStorage.setItem("resumoAtual", JSON.stringify(base));
      } catch {}
    } catch (error) {
      console.error(error);
      setObjSnackBar({ type: "error", msg: "Ops, temos um erro aqui! Contate o suporte." });
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [dados?.idEmpresa, dados?.contasResumo, dados?.verbasAjustadas, dados?.resumo]);

  // monta ao entrar (ou quando idEmpresa/idVersao/dados carregados mudarem)
  useEffect(() => {
    fetchData();
  }, [fetchData, dados.idEmpresa, dados.idVersao, dados.contasResumo]);

  // Cria um hash baseado nas verbas que compõem projeto para detectar mudanças
  const hashComposicaoProjeto = useMemo(() => {
    if (!dados?.verbasAjustadas) return '';
    return JSON.stringify(
      (dados.verbasAjustadas || [])
        .filter(v => v.composicaoProjeto === true)
        .map(v => ({
          codigo: v.codigoRubrica || v.codRubrica,
          inicio: v.inicioValidade || v.inicio,
          fim: v.fimValidade || v.fim,
          composicao: v.composicaoProjeto
        }))
        .sort((a, b) => {
          const keyA = `${a.codigo}|${a.inicio}|${a.fim}`;
          const keyB = `${b.codigo}|${b.inicio}|${b.fim}`;
          return keyA.localeCompare(keyB);
        })
    );
  }, [dados?.verbasAjustadas]);

  // Atualiza automaticamente quando composicaoProjeto mudar nas verbasAjustadas
  const prevHashRef = useRef('');
  const hashTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Limpar timeout quando componente desmontar
      if (hashTimeoutRef.current) {
        clearTimeout(hashTimeoutRef.current);
        hashTimeoutRef.current = null;
      }
    };
  }, []);
  
  useEffect(() => {
    if (!dados?.idEmpresa || !hashComposicaoProjeto || !isMountedRef.current) return;
    
    // Só atualiza se o hash realmente mudou (não na primeira renderização)
    if (prevHashRef.current === hashComposicaoProjeto) return;
    prevHashRef.current = hashComposicaoProjeto;
    
    // Limpar timeout anterior se existir
    if (hashTimeoutRef.current) {
      clearTimeout(hashTimeoutRef.current);
      hashTimeoutRef.current = null;
    }
    
    // Debounce para evitar múltiplas chamadas rápidas
    hashTimeoutRef.current = setTimeout(() => {
      // Verificar se ainda está montado e não está fazendo fetch
      if (isMountedRef.current && !fetchingRef.current) {
        fetchData();
      }
      hashTimeoutRef.current = null;
    }, 500);

    return () => {
      if (hashTimeoutRef.current) {
        clearTimeout(hashTimeoutRef.current);
        hashTimeoutRef.current = null;
      }
    };
  }, [hashComposicaoProjeto, dados.idEmpresa, fetchData]);

  useEffect(() => {
    let columns = [];
    if (filters.calculoFilter.calculo === "cprb" && filters.calculoFilter.sefip) {
      columns = handleChangeColumns("cprb-with-sefip");
    } else if (filters.calculoFilter.calculo === "cprb" && !filters.calculoFilter.sefip) {
      columns = handleChangeColumns("cprb");
    } else if (filters.calculoFilter.calculo === "geral" && filters.calculoFilter.sefip) {
      columns = handleChangeColumns("sefip");
    } else {
      columns = handleChangeColumns();
    }
    setDataColumns(columns);
  }, [filters.calculoFilter]);

  useEffect(() => {
    const filtraPorRange = () => {
      const hasDtInicial = !!filters.datas.dtInicial;
      const hasDtFinal = !!filters.datas.dtFinal;

      if (!hasDtInicial && !hasDtFinal) {
        setDataTable((prev) => ({ ...prev, dataFilter: prev.data }));
        return;
      }

      const dtInicio = hasDtInicial ? new Date(filters.datas.dtInicial) : null;
      const dtFinal = hasDtFinal ? new Date(filters.datas.dtFinal) : null;

      let mesInicio, anoInicio, mesFim, anoFim;
      if (dtInicio) {
        mesInicio = parseInt(String(dtInicio.getMonth() + 1).padStart(2, "0"));
        anoInicio = dtInicio.getFullYear();
      }
      if (dtFinal) {
        mesFim = parseInt(String(dtFinal.getMonth() + 1).padStart(2, "0"));
        anoFim = dtFinal.getFullYear();
      }

      const dataFilterRange = dataTable.data.filter((item) => {
        const [objMes, objAno] = item.periodoApuracao.split("/").map(Number);
        const dataItem = objAno * 100 + objMes;

        if (dtInicio && dtFinal) {
          const dataInicio = anoInicio * 100 + mesInicio;
          const dataFim = anoFim * 100 + mesFim;
          return dataItem >= dataInicio && dataItem <= dataFim;
        } else if (dtInicio) {
          const dataInicio = anoInicio * 100 + mesInicio;
          return dataItem >= dataInicio;
        } else if (dtFinal) {
          const dataFim = anoFim * 100 + mesFim;
          return dataItem <= dataFim;
        }
        return true;
      });

      setDataTable((prev) => ({ ...prev, dataFilter: dataFilterRange }));
    };

    dataTable.data.length > 0 && filtraPorRange();
  }, [filters.datas.dtInicial, filters.datas.dtFinal, dataTable.data]);

  const handleChangeColumns = (context) => {
    switch (context) {
      case "cprb":
        return [
          { key: "periodoApuracao", label: "Período" },
          { key: "codigoRubrica", label: "Código" },
          { key: "descricaoRubrica", label: "Descrição" },
          { key: "incidenciaRubrica", label: "Incidência" },
          { key: "valorRubrica", label: "Valor" },
          { key: "totalCprb", label: "CPRB" },
          { key: "totalCprbCorrigido", label: "CPRB Corrigido" },
          { key: "totalCprbCalculado", label: "CPRB Correção" },
        ];
      case "sefip":
        return [
          { key: "cnpj", label: "CNPJ" },
          { key: "periodoApuracao", label: "Período" },
          { key: "totalGeralCorrigido", label: "Geral Corrigido" },
          { key: "cargaSefipCprb", label: "Carga Sefip" },
        ];
      case "cprb-with-sefip":
        return [
          { key: "cnpj", label: "CNPJ" },
          { key: "periodoApuracao", label: "Período" },
          { key: "totalCprbCorrigido", label: "CPRB Corrigido" },
          { key: "cargaSefipCprb", label: "Carga Sefip" },
        ];
      default:
        return [
          { key: "periodoApuracao", label: "Período" },
          { key: "codigoRubrica", label: "Código" },
          { key: "descricaoRubrica", label: "Descrição" },
          { key: "incidenciaRubrica", label: "Incidência" },
          { key: "valorRubrica", label: "Valor" },
          { key: "totalGeral", label: "Geral" },
          { key: "totalGeralCorrigido", label: "Geral Corrigido" },
          { key: "totalGeralCalculado", label: "Geral Correção" },
        ];
    }
  };

  const handleFiltersCalculoNew = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      calculoFilter: {
        ...prev.calculoFilter,
        [name]: name === "sefip" ? !prev.calculoFilter.sefip : value,
      },
    }));
  };

  // Helper para normalizar datas em YYYY-MM quando possível (aceita Date ou string)
  const formatDateForQuery = (value) => {
    if (!value) return null;
    try {
      // se for Date
      if (value instanceof Date) {
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, "0");
        return `${y}-${m}`;
      }
      // se for string no formato yyyy-mm ou yyyy-mm-dd já aceitável
      const s = String(value);
      if (/^\d{4}-\d{2}(-\d{2})?$/.test(s)) return s.slice(0, 7);
      if (/^\d{6,8}$/.test(s)) return s.slice(0, 6).replace(/(\d{4})(\d{2})/, "$1-$2");
      return s;
    } catch {
      return null;
    }
  };

  // persist helper (synchronous) — evita condição de corrida quando o usuário clica gerar imediatamente após trocar filtros
  const persistRelatorioGeralFilters = (nextFilters) => {
    try {
      const datas = nextFilters?.datas ?? filters.datas ?? { dtInicial: null, dtFinal: null };
      const verbas = nextFilters?.verbas ?? (nextFilters?.tipoItem ?? filters.verbas) ?? null;
      const inicio = datas?.dtInicial ? formatDateForQuery(datas.dtInicial) : null;
      const fim = datas?.dtFinal ? formatDateForQuery(datas.dtFinal) : null;
      const tipoItem = verbas ? String(verbas).toLowerCase() : null;
      localStorage.setItem("relatorioGeralFilters", JSON.stringify({ inicio, fim, tipoItem }));
    } catch {}
  };

  const handleFilterVerbas = (value) => {
    setFilters((prev) => ({ ...prev, verbas: value ? value : "Completo" }));
    setDataTable((prev) => {
      if (value === "Completo") return { ...prev, dataFilter: prev.data };
      const filterVerbas = prev.data.filter((i) => i.tipoRubrica === value);
      return { ...prev, dataFilter: filterVerbas };
    });
    // persist immediately so MainComponente can read tipoItem without race
    try { persistRelatorioGeralFilters({ ...filters, verbas: value ? value : 'Completo' }); } catch {}
  };

  const handleChangeDate = (value, name) => {
    setFilters((prev) => ({ ...prev, datas: { ...prev.datas, [name]: value } }));
    // persist the updated date immediately
    try {
      const nextDatas = { ...(filters.datas || {}), [name]: value };
      persistRelatorioGeralFilters({ ...filters, datas: nextDatas });
    } catch {}
  };

  // persistir filtros de data para serem usados por outras rotinas (ex.: geração de relatório consolidado)
  useEffect(() => {
    try {
      const inicio = filters.datas.dtInicial ? formatDateForQuery(filters.datas.dtInicial) : null;
      const fim = filters.datas.dtFinal ? formatDateForQuery(filters.datas.dtFinal) : null;
      const tipoItem = filters.verbas ? String(filters.verbas).toLowerCase() : null;
      localStorage.setItem("relatorioGeralFilters", JSON.stringify({ inicio, fim, tipoItem }));
    } catch {}
  }, [filters.datas, filters.verbas]);

  const handleAutocompleteChange = (value) => {
    setFilters((prev) => ({ ...prev, contas: value ? value : [] }));
  };

  // helpers for legacy report format
  const toTwoDecimals = (v) => {
    try {
      if (v == null || v === "") return 0;
      if (typeof v === "number") return Number(Number(v).toFixed(2));
      // accept strings like 'R$ 1.234,56' or '1.234,56' and convert to Number
      let s = String(v).trim();
      // remove currency symbols and non-breaking spaces
      s = s.replace(/[R$\u00A0\s]/g, "");
      // if looks like a period string (e.g., '01/2024'), do not parse
      if (s.includes('/')) return 0;
      // unify decimal separator: remove thousand separators and replace comma
      s = s.replace(/\./g, "").replace(/,/g, ".");
      const n = parseFloat(s);
      return isNaN(n) ? 0 : Number(n.toFixed(2));
    } catch {
      return 0;
    }
  };

  const toMMYYYY = (p) => {
    if (!p && p !== 0) return "";
    const s = String(p);
    if (s.includes("/")) return s;
    if (/^\d{6}$/.test(s)) {
      const y = s.slice(0, 4);
      const m = s.slice(4);
      return `${m}/${y}`;
    }
    if (/^\d{4}-\d{2}$/.test(s)) {
      return s.slice(5) + '/' + s.slice(0,4);
    }
    return s;
  };

  const parsePeriodoKey = (p) => {
    try {
      if (!p && p !== 0) return '';
      const s = String(p);
      // already in MM/YYYY
      if (/^\d{2}\/\d{4}$/.test(s)) {
        const [mm, yy] = s.split('/');
        return `${yy}${mm}`; // YYYYMM
      }
      // already YYYY-MM or YYYYMM
      if (/^\d{4}-\d{2}$/.test(s)) return s.replace('-', '');
      if (/^\d{6}$/.test(s)) return s;
      return s;
    } catch { return String(p); }
  };

  const obterRelatorio = async (visibleData) => {
    const sefipRelatorio = !!filters.calculoFilter.sefip;
    return sefipRelatorio ? obterRelatorioSefip(visibleData) : obterRelatorioGeral(visibleData);
  };

  const findValueByKey = (obj, key, visited = new Set(), depth = 0) => {
    if (!obj || typeof obj !== 'object' || visited.has(obj) || depth > 6) return undefined;
    visited.add(obj);
    if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
    for (const k of Object.keys(obj)) {
      try {
        const v = obj[k];
        if (k === key && v !== undefined) return v;
        if (v && typeof v === 'object') {
          const found = findValueByKey(v, key, visited, depth + 1);
          if (found !== undefined) return found;
        }
      } catch {}
    }
    return undefined;
  };

  const getFieldNumber = (obj, keys) => {
    try {
      for (const k of keys) {
        if (!obj) continue;
        // direct
        let v = obj[k];
        if (v === undefined) v = findValueByKey(obj, k);
        if (v !== undefined && v !== null && v !== "") return toTwoDecimals(v);
      }
    } catch {}
    return 0;
  };

  const getFieldString = (obj, keys) => {
    try {
      for (const k of keys) {
        if (!obj) continue;
        let v = obj[k];
        if (v === undefined) v = findValueByKey(obj, k);
        if (v !== undefined && v !== null && String(v).trim() !== "") return String(v);
      }
    } catch {}
    return "";
  };

  const normalizeCnpj = (raw) => {
    try {
      if (!raw) return "";
      const s = String(raw);
      const digits = s.replace(/\D/g, "");
      // if we somehow have only 8 digits (branch), try to find alternate full cnpj in object
      if (digits.length === 8) return digits; // keep as-is, upstream may have only branch
      return digits;
    } catch { return String(raw); }
  };

  const getPeriodo = (obj) => {
    try {
      // try common fields first
      let p = getFieldString(obj, ['periodoApuracao','periodo','competencia','mesAno','period','periodoFormatado','periodoFormat']);
      if (!p || p === '0' || p === '1') {
        // try month/year pairs
        const mes = getFieldString(obj, ['mes','mesCompetencia']);
        const ano = getFieldString(obj, ['ano','anoCompetencia','year']);
        if (mes && ano) {
          const mm = String(mes).padStart(2,'0');
          return `${mm}/${String(ano).slice(0,4)}`;
        }
        // try numeric like 202301
        const alt = getFieldString(obj, ['periodoRef','periodoReferencia']);
        if (alt && /^\d{6}$/.test(String(alt))) return toMMYYYY(String(alt));
      }
      // standardize known formats
      return toMMYYYY(p);
    } catch {
      return '';
    }
  };

  const obterRelatorioGeral = async (currentData) => {
    // Gera relatório diretamente a partir do resumoAtual (currentData) já filtrado
    const reportData = [];
    let fileName = `resumo_geral`;
    let cnpj;

    // agrupar por período (usa periodoApuracao quando disponível)
    const mapPorPeriodo = new Map();
    currentData.forEach((item) => {
      if (!cnpj) cnpj = item.cnpj;
      const periodoKey = item.periodoApuracao || getPeriodo(item) || '';
      if (!mapPorPeriodo.has(periodoKey)) mapPorPeriodo.set(periodoKey, []);
      mapPorPeriodo.get(periodoKey).push(item);
    });

    // ordenar períodos
    const periodosOrdenados = Array.from(mapPorPeriodo.keys()).sort((a, b) => {
      const ka = parsePeriodoKey(a || '');
      const kb = parsePeriodoKey(b || '');
      if (ka === kb) return 0;
      return ka > kb ? 1 : -1;
    });

    const somasTotais = {
      valorBase: 0,
      inss: 0,
      terceiros: 0,
      rat: 0,
      total: 0,
      inssCorrigido: 0,
      terceirosCorrigido: 0,
      ratCorrigido: 0,
      totalCorrigido: 0,
      valorCorrecao: 0,
    };

    const calculoRelatorio = filters.calculoFilter.calculo || 'geral';

    for (const periodo of periodosOrdenados) {
      const itens = mapPorPeriodo.get(periodo) || [];

      // ordenar por codigoRubrica / idTabelaRubrica para estabilidade
      itens.sort((a, b) => {
        if ((a.codigoRubrica || '') < (b.codigoRubrica || '')) return -1;
        if ((a.codigoRubrica || '') > (b.codigoRubrica || '')) return 1;
        if ((a.idTabelaRubrica || 0) < (b.idTabelaRubrica || 0)) return -1;
        if ((a.idTabelaRubrica || 0) > (b.idTabelaRubrica || 0)) return 1;
        return 0;
      });

      const somasMensal = {
        valorBase: 0,
        inss: 0,
        terceiros: 0,
        rat: 0,
        total: 0,
        inssCorrigido: 0,
        terceirosCorrigido: 0,
        ratCorrigido: 0,
        totalCorrigido: 0,
        valorCorrecao: 0,
      };

      itens.forEach((it) => {
        const valorBase = getFieldNumber(it, ['valorRubrica', 'valorBase', 'valor']);
        const inss = getFieldNumber(it, ['inss', 'valorInss']);
        const terceiros = getFieldNumber(it, ['terceiros', 'valorTerceiros']);
        const rat = getFieldNumber(it, ['rat']);
        let total = calculoRelatorio === 'geral' ? getFieldNumber(it, ['totalGeral', 'total']) : getFieldNumber(it, ['totalCprb']);
        const inssCorrigido = getFieldNumber(it, ['inssCorrigido', 'inss_corrigido']);
        const terceirosCorrigido = getFieldNumber(it, ['terceirosCorrigido', 'terceiros_corrigido']);
        const ratCorrigido = getFieldNumber(it, ['ratCorrigido', 'rat_corrigido']);
        let totalCorrigido = calculoRelatorio === 'geral' ? getFieldNumber(it, ['totalGeralCorrigido', 'total_geral_corrigido']) : getFieldNumber(it, ['totalCprbCorrigido']);
        const selic = getFieldNumber(it, ['selic']);
        // valor de correção: usa totalGeralCalculado/valorCorrecao para 'geral', senão totalCprbCalculado
        let valorCorrecao = calculoRelatorio === 'geral'
          ? getFieldNumber(it, ['totalGeralCalculado', 'valorCorrecao'])
          : getFieldNumber(it, ['totalCprbCalculado']);

        // fallback heuristics: if totals are zero but components exist, compute them
        try {
          const sumComponents = Number((valorBase + inss + terceiros + rat).toFixed(2));
          if ((!total || total === 0) && sumComponents !== 0) {
            total = sumComponents;
          }
          const sumCorrigido = Number((inssCorrigido + terceirosCorrigido + ratCorrigido).toFixed(2));
          if ((!totalCorrigido || totalCorrigido === 0) && sumCorrigido !== 0) {
            totalCorrigido = sumCorrigido;
          }
          if ((!valorCorrecao || valorCorrecao === 0) && totalCorrigido && total && (totalCorrigido !== total)) {
            valorCorrecao = Number((totalCorrigido - total).toFixed(2));
          }
        } catch (e) {}

        // LINHAS RELATORIO: use grouped periodo when available
        reportData.push({
          'Período': periodo || (it.periodoApuracao || getPeriodo(it)),
          'CNPJ': getFieldString(it, ['cnpj', 'cnpjCliente', 'cnpjEmpresa', 'empresaCnpj', 'clienteCnpj', 'cpfCnpj']) || '',
          'Código': it.codigoRubrica_cellVal || it.codigoRubrica || '',
          'Evento': it.descricaoRubrica || '',
          'Incidência': it.incidenciaRubrica || '',
          'Valor Base': valorBase,
          'INSS': inss,
          'Terceiros': terceiros,
          'Rat': rat,
          'Total': total,
          'INSS Corrigido': inssCorrigido,
          'Terceiros Corrigido': terceirosCorrigido,
          'Rat Corrigido': ratCorrigido,
          'Total Corrigido': totalCorrigido,
          'Selic': selic,
          'Valor Correção': valorCorrecao,
        });

        somasMensal.valorBase += valorBase;
        somasMensal.inss += inss;
        somasMensal.terceiros += terceiros;
        somasMensal.rat += rat;
        somasMensal.total += total;
        somasMensal.inssCorrigido += inssCorrigido;
        somasMensal.terceirosCorrigido += terceirosCorrigido;
        somasMensal.ratCorrigido += ratCorrigido;
        somasMensal.totalCorrigido += totalCorrigido;
        somasMensal.valorCorrecao += valorCorrecao;
      });

      // subtotal por mês
      reportData.push({
        'Período': '',
        'CNPJ': '',
        'Código': '',
        'Evento': '',
        'Incidência': 'Sub Total Mês ' + toMMYYYY(periodo),
        'Valor Base': somasMensal.valorBase,
        'INSS': somasMensal.inss,
        'Terceiros': somasMensal.terceiros,
        'Rat': somasMensal.rat,
        'Total': somasMensal.total,
        'INSS Corrigido': somasMensal.inssCorrigido,
        'Terceiros Corrigido': somasMensal.terceirosCorrigido,
        'Rat Corrigido': somasMensal.ratCorrigido,
        'Total Corrigido': somasMensal.totalCorrigido,
        'Selic': '',
        'Valor Correção': somasMensal.valorCorrecao,
      });

      // linha espaçadora
      reportData.push({
        'Período': '', 'CNPJ': '', 'Código': '', 'Evento': '', 'Incidência': '', 'Valor Base': '', 'INSS': '', 'Terceiros': '', 'Rat': '', 'Total': '', 'INSS Corrigido': '', 'Terceiros Corrigido': '', 'Rat Corrigido': '', 'Total Corrigido': '', 'Selic': '', 'Valor Correção': ''
      });

      // acumula nos totais gerais
      somasTotais.valorBase += somasMensal.valorBase;
      somasTotais.inss += somasMensal.inss;
      somasTotais.terceiros += somasMensal.terceiros;
      somasTotais.rat += somasMensal.rat;
      somasTotais.total += somasMensal.total;
      somasTotais.inssCorrigido += somasMensal.inssCorrigido;
      somasTotais.terceirosCorrigido += somasMensal.terceirosCorrigido;
      somasTotais.ratCorrigido += somasMensal.ratCorrigido;
      somasTotais.totalCorrigido += somasMensal.totalCorrigido;
      somasTotais.valorCorrecao += somasMensal.valorCorrecao;
    }

    // total relatório
    reportData.push({
      'Período': '',
      'CNPJ': '',
      'Código': '',
      'Evento': '',
      'Incidência': 'Total Relatório',
      'Valor Base': somasTotais.valorBase,
      'INSS': somasTotais.inss,
      'Terceiros': somasTotais.terceiros,
      'Rat': somasTotais.rat,
      'Total': somasTotais.total,
      'INSS Corrigido': somasTotais.inssCorrigido,
      'Terceiros Corrigido': somasTotais.terceirosCorrigido,
      'Rat Corrigido': somasTotais.ratCorrigido,
      'Total Corrigido': somasTotais.totalCorrigido,
      'Selic': '',
      'Valor Correção': somasTotais.valorCorrecao,
    });

    if (cnpj) fileName = `resumo_geral_${cnpj.replace(/\D/g, '')}`;
    return [reportData, fileName];
  };

  const obterRelatorioSefip = async (currentData) => {
    let folhaData = [];
    let fileName = `relatorio_sefip`;
    let cnpj;

    currentData.forEach((verba) => {
      if (!cnpj) cnpj = verba.cnpj;
    });

    let periodos = [...currentData];
    periodos = periodos.sort((a, b) => (String(a.periodoApuracao) > String(b.periodoApuracao) ? 1 : -1));

    periodos.forEach((periodo) => {
      folhaData.push({
        "CNPJ": (periodo.cnpj || '').replace(/[\.\/\-]/g, ""),
        "Período": periodo.periodoApuracao,
        "Total Corrigido": filters.calculoFilter.calculo === 'geral' ? toTwoDecimals(periodo.totalGeralCorrigido) : toTwoDecimals(periodo.totalCprbCorrigido),
        "Carga Sefip": filters.calculoFilter.calculo === 'geral' ? periodo.cargaSefipGeral : periodo.cargaSefipCprb,
      });
    });

    if (cnpj) fileName = `relatorio_sefip_${cnpj.replace(/\D/g, '')}`;
    return [folhaData, fileName];
  };

   const handleExportRelatorioCompleto = async () => {
     try {
       // Preferir resumoAtual salvo no localStorage (origem 'ressumoAtual' é o que o usuário afirmou ser o correto)
       const cargaResumoAtual = (() => {
         try {
           const raw = localStorage.getItem('resumoAtual');
           if (!raw) return [];
           const parsed = JSON.parse(raw);
           return Array.isArray(parsed) ? parsed : [];
         } catch (e) {
           return [];
         }
       })();

       const periodoToNumber = (p) => {
         try {
           if (!p && p !== 0) return null;
           const s = String(p).trim();
           if (/^\d{2}\/\d{4}$/.test(s)) {
             const [mm, yy] = s.split('/').map(Number);
             return yy * 100 + mm;
           }
           if (/^\d{6}$/.test(s)) return Number(s);
           if (/^\d{4}-\d{2}$/.test(s)) return Number(s.replace('-', ''));
           // fallback parse via parsePeriodoKey (retorna YYYYMM quando possível)
           const key = String(parsePeriodoKey(s || '') || '').trim();
           if (/^\d{6}$/.test(key)) return Number(key);
           return null;
         } catch { return null; }
       };

       // Use resumoAtual from localStorage as the canonical base for export.
       // Fallback to dataTable.dataFilter only if resumoAtual is empty or missing.
       let visibleData = Array.isArray(cargaResumoAtual) && cargaResumoAtual.length > 0
         ? cargaResumoAtual.slice()
         : (Array.isArray(dataTable.dataFilter) && dataTable.dataFilter.length > 0
             ? dataTable.dataFilter.slice()
             : (Array.isArray(dataTable.data) ? dataTable.data.slice() : []));

      // ensure array
      visibleData = Array.isArray(visibleData) ? visibleData : [];

      // aplicar filtro de contas (se houver) — tenta casar por campos comuns
      if (Array.isArray(filters.contas) && filters.contas.length > 0) {
        const contasSet = new Set(filters.contas.map(c => (typeof c === 'string' ? c : (c.id ?? c.value ?? c.codigo ?? c.nome ?? c.label ?? String(c))).toString()));
        visibleData = visibleData.filter(item => {
          try {
            const candidates = [item.conta, item.contaId, item.idConta, item.codigoConta, item.contaCodigo, item.contaDescricao];
            for (const cand of candidates) {
              if (!cand) continue;
              const val = (typeof cand === 'object' ? (cand.id ?? cand.value ?? cand.codigo ?? cand.nome ?? cand.label ?? '') : String(cand));
              if (val && contasSet.has(String(val))) return true;
            }
            // fallback: test if any filter string is contained in JSON of item
            for (const c of filters.contas) {
              const cv = typeof c === 'string' ? c : (c.id ?? c.value ?? c.codigo ?? c.nome ?? c.label ?? '');
              if (cv && JSON.stringify(item).includes(String(cv))) return true;
            }
          } catch {}
          return false;
        });
      }

      // debug: mostrar origem e quantidade inicial após aplicar contas
      try { console.log('Export base source: resumoAtual length:', Array.isArray(cargaResumoAtual) ? cargaResumoAtual.length : 'n/a', 'visibleData initial length:', visibleData.length); } catch (e) {}

       // aplicar filtro de verbas (se selecionado)
       if (filters.verbas && String(filters.verbas).toLowerCase() !== 'completo') {
         const verbaFilter = String(filters.verbas || '').toLowerCase();
         try { console.log('Applying verbas filter:', filters.verbas, 'verbaFilter(normalized):', verbaFilter); } catch (e) {}

         // Use helper getFieldString to try multiple possible field names that may contain
         // the verba/tipo information (tipoRubrica, tipo, tipo_rubrica, categoria, etc.)
         visibleData = visibleData.filter((it) => {
           try {
             const candidate = String(getFieldString(it, ['tipoRubrica','tipo','tipo_rubrica','tipoRubricaDescricao','categoria','categoriaVerba','rubricaTipo']) || '')
               .trim()
               .toLowerCase();
             return candidate === verbaFilter;
           } catch (e) {
             return false;
           }
         });
       }

       // aplicar filtro de data (range) sobre periodoApuracao
       if (filters.datas && (filters.datas.dtInicial || filters.datas.dtFinal)) {
         const hasDtInicial = !!filters.datas.dtInicial;
         const hasDtFinal = !!filters.datas.dtFinal;
         let mesInicio, anoInicio, mesFim, anoFim;
         if (hasDtInicial) {
           const dt = new Date(filters.datas.dtInicial);
           mesInicio = parseInt(String(dt.getMonth() + 1).padStart(2, '0'));
           anoInicio = dt.getFullYear();
         }
         if (hasDtFinal) {
           const dt = new Date(filters.datas.dtFinal);
           mesFim = parseInt(String(dt.getMonth() + 1).padStart(2, '0'));
           anoFim = dt.getFullYear();
         }
         const inicioKey = hasDtInicial ? (anoInicio * 100 + mesInicio) : null;
         const fimKey = hasDtFinal ? (anoFim * 100 + mesFim) : null;

         visibleData = visibleData.filter((item) => {
           try {
             const p = item.periodoApuracao || getPeriodo(item) || '';
             const n = periodoToNumber(p);
             if (n == null) return false;
             if (inicioKey != null && fimKey != null) return n >= inicioKey && n <= fimKey;
             if (inicioKey != null) return n >= inicioKey;
             if (fimKey != null) return n <= fimKey;
             return true;
           } catch (e) {
             return false;
           }
         });
       }

      // debug: mostrar quantos itens restaram antes de gerar o relatório
      try { console.log('visibleData length after filters:', Array.isArray(visibleData) ? visibleData.length : 'n/a'); } catch (e) {}
       // DEBUG: fetch resumo da versao se possível
       try {
         if (typeof handleBuscarResumoGeralVersao === 'function') {
           const resumoVersao = await handleBuscarResumoGeralVersao();
           console.log('handleBuscarResumoGeralVersao result (sample):', Array.isArray(resumoVersao) ? resumoVersao.slice(0, 20) : resumoVersao);
         }
       } catch (e) {
         console.warn('erro ao chamar handleBuscarResumoGeralVersao:', e);
       }

       logDebugData(visibleData);

       if (!visibleData || visibleData.length === 0) {
         setObjSnackBar({ type: "error", msg: "Nenhum dado disponível para exportar." });
         return;
       }

       // DEBUG: log visibleData before building report
       try { console.log('Export: visibleData length before gerarRelatorio:', Array.isArray(visibleData) ? visibleData.length : 'n/a', 'visibleData sample:', Array.isArray(visibleData) ? visibleData.slice(0,5) : visibleData); } catch (e) {}

       // Normalizar campos monetários que vêm como strings (ex.: 'R$ 1.234,56') para Number
      try {
        const numericKeys = [
          'valorRubrica','valorBase','valor','inss','valorInss','terceiros','valorTerceiros','rat',
          'totalGeral','totalCprb','total','totalCprb','totalGeralCorrigido','totalCprbCorrigido',
          'inssCorrigido','inss_corrigido','terceirosCorrigido','terceiros_corrigido','ratCorrigido','rat_corrigido',
          'totalGeralCalculado','totalCprbCalculado','valorCorrecao','selic','taxa'
        ];
        visibleData = visibleData.map((it) => {
          if (!it || typeof it !== 'object') return it;
          const copy = { ...it };
          numericKeys.forEach((k) => {
            try {
              if (copy[k] !== undefined && copy[k] !== null) {
                copy[k] = toTwoDecimals(copy[k]);
              }
            } catch (e) {}
          });
          return copy;
        });
        console.log('Export: normalized visibleData sample (first 5):', visibleData.slice(0,5).map(it => ({ periodo: it.periodoApuracao || it.periodo, valorRubrica: it.valorRubrica, inss: it.inss, totalGeral: it.totalGeral })));
      } catch (e) { console.warn('Erro ao normalizar valores numéricos para export:', e); }

      const [reportData, fileName] = await obterRelatorio(visibleData);

       // DEBUG: validate reportData and print sample
       try {
         console.log('Export: reportData length:', Array.isArray(reportData) ? reportData.length : 'n/a', 'fileName:', fileName);
         console.log('Export: reportData sample (up to 5 rows):', Array.isArray(reportData) ? reportData.slice(0,5) : reportData);
       } catch (e) {}

       if (!reportData || !Array.isArray(reportData) || reportData.length === 0) {
         console.error('Relatório gerado sem linhas. visibleData length:', Array.isArray(visibleData) ? visibleData.length : 'n/a');
         setObjSnackBar({ type: "error", msg: "Nenhum dado disponível para exportar (relatório vazio)." });
         return;
       }

       // Montar segunda aba: Resumo das Categorias (idêntico ao relatório cinza/azul da prévia)
       let abaResumoCategorias = null;
       try {
         const relFilters = JSON.parse(localStorage.getItem("relatorioGeralFilters") || "{}") || {};
         const params = {};
         if (relFilters?.inicio) params.inicio = relFilters.inicio;
         if (relFilters?.fim) params.fim = relFilters.fim;
         if (relFilters?.tipoItem) params.tipoItem = relFilters.tipoItem;

         const isProjeto = dados?.tipoVersao === "projeto";
         const idVersao = dados?.idVersao;
         if (idVersao) {
           const resp = isProjeto
             ? await gerarRelatorioProjetoInssConsolidado(idVersao, params)
             : await gerarRelatorioPreviaInssConsolidado(idVersao, params);
           const dadosPreviaPDF = resp?.resumo || resp || [];

           // Calcular bases atualizadas (Limitação 20s) - mesma lógica do ModalPreviaPDF
           const TAXA_20S = 5.8;
           const VALOR_BASE = 30360;
           const parsePeriod = (p) => {
             if (!p) return null;
             const parts = String(p).split("-");
             if (parts.length < 2) return null;
             return Number(parts[0]) * 100 + Number(parts[1]);
           };
           const inicioNum = parsePeriod(relFilters?.inicio);
           const fimNum = parsePeriod(relFilters?.fim);
           const bases = Array.isArray(dados?.baseCalculo) ? dados.baseCalculo : [];
           const computeValorAtualizado = (base) => {
             const valor = Number(base.valor) || 0;
             const diferencaBase = valor - VALOR_BASE > 0 ? valor - VALOR_BASE : 0;
             const calculo20s = diferencaBase > 0 ? (diferencaBase * TAXA_20S) / 100 : 0;
             const taxaForRef = base.taxaSelic != null ? Number(base.taxaSelic) : 0;
             return calculo20s + (calculo20s * (taxaForRef / 100) || 0);
           };
           const basesAtualizadas = bases
             .filter((b) => {
               if (!b?.referencia) return false;
               const n = parsePeriod(b.referencia);
               if (!n) return false;
               if (inicioNum && fimNum) return n >= inicioNum && n <= fimNum;
               if (inicioNum) return n >= inicioNum;
               if (fimNum) return n <= fimNum;
               return true;
             })
             .map((b) => ({ valorAtualizado: computeValorAtualizado(b) }));
           const totalAtualizado = basesAtualizadas.reduce((s, b) => s + (b.valorAtualizado || 0), 0);

           const totalGeralBase = dadosPreviaPDF.reduce((acc, it) => acc + (it.valor || 0), 0);
           const totalGeral = totalGeralBase + totalAtualizado;
           const totalDesnecessidade = dadosPreviaPDF
            .filter(
              (it) =>
                normalizarCategoriaMedidaJudicial(it.medidaJudicialCategoria) ===
                "DESNECESSIDADE"
            )
             .reduce((acc, it) => acc + (it.valor || 0), 0);
           const totalSugestaoBase = dadosPreviaPDF
            .filter(
              (it) =>
                normalizarCategoriaMedidaJudicial(it.medidaJudicialCategoria) ===
                "SUGESTAO"
            )
             .reduce((acc, it) => acc + (it.valor || 0), 0);
           const totalSugestao = totalSugestaoBase + totalAtualizado;

           const rows = [];
           dadosPreviaPDF.forEach((item) => {
            const categoria = normalizarCategoriaMedidaJudicial(
              item.medidaJudicialCategoria
            );
             const cor =
              categoria === "SUGESTAO" ? "#dceefb"
              : categoria === "DESNECESSIDADE" ? "#e3e3e3"
               : "#ffffff";
             rows.push({
               item: "",
               verba: item.descricaoCategoria || "",
               fundamentacao: item.fundamentacaoCategoria || "",
               valor: Number(item.valor) || 0,
               cor,
             });
           });
           if (basesAtualizadas.length > 0) {
             rows.push({
               item: "",
               verba: "Limitação 20 Salários",
               fundamentacao: "Artigo 4º da Lei nº 6.950/1981 (cálculo funcionário x funcionário - salário de contribuição)",
               valor: totalAtualizado,
               cor: "#dceefb",
             });
           }
           rows.push({ item: "", verba: "", fundamentacao: "Total", valor: totalGeral, bold: true });
           rows.push({
             item: "",
             verba: "",
             fundamentacao: "Desnecessidade de medida judicial",
             valor: totalDesnecessidade,
             cor: "#e3e3e3",
             bold: true,
           });
           rows.push({
             item: "",
             verba: "",
             fundamentacao: "Sugestão de medida judicial",
             valor: totalSugestao,
             cor: "#dceefb",
             bold: true,
           });
           abaResumoCategorias = { sheetName: "Resumo das Categorias", rows };
         }
       } catch (err) {
         console.warn("Erro ao obter resumo consolidado para segunda aba:", err);
       }

       const exportPayload = abaResumoCategorias
         ? [reportData, fileName || "relatorio_geral", abaResumoCategorias]
         : [reportData, fileName || "relatorio_geral"];
       await exportExcel(exportPayload);
       setObjSnackBar({ type: "success", msg: "Arquivo Excel gerado com sucesso!" });
     } catch (error) {
       console.error(error);
       setObjSnackBar({ type: "error", msg: "Erro ao exportar para Excel" });
     }
   };

  const collectKeysRecursive = (obj, keys = new Set(), prefix = '', depth = 0, maxDepth = 3, visited = new Set()) => {
    if (!obj || typeof obj !== 'object' || visited.has(obj) || depth > maxDepth) return keys;
    visited.add(obj);
    Object.keys(obj).forEach((k) => {
      const full = prefix ? `${prefix}.${k}` : k;
      keys.add(full);
      try {
        const v = obj[k];
        if (v && typeof v === 'object') collectKeysRecursive(v, keys, full, depth + 1, maxDepth, visited);
      } catch {}
    });
    return keys;
  };

  const logDebugData = (visibleData) => {
    try {
      console.groupCollapsed('Relatorio DEBUG — amostra de dados');
      console.log('filters:', filters);
      console.log('dataColumns:', dataColumns);
      console.log('optionsContas length:', (optionsContas || []).length);
      try {
        console.log('context.dados (full):', dados);
        console.log('dados.resumo length:', (dados && dados.resumo && Array.isArray(dados.resumo)) ? dados.resumo.length : 'n/a');
        console.log('dados.contasResumo length:', (dados && dados.contasResumo && Array.isArray(dados.contasResumo)) ? dados.contasResumo.length : 'n/a');
      } catch (e) {
        console.warn('erro lendo dados do contexto:', e);
      }
      try {
        console.log('localStorage.resumoAtual:', localStorage.getItem('resumoAtual'));
        console.log('localStorage.relatorioGeralFilters:', localStorage.getItem('relatorioGeralFilters'));
      } catch (e) {
        console.warn('sem acesso a localStorage:', e);
      }
      const sample = Array.isArray(visibleData) ? visibleData.slice(0, 10) : visibleData;
      console.log('visibleData length:', Array.isArray(visibleData) ? visibleData.length : 0);
      console.log('visibleData sample (up to 10):', sample);
      console.groupEnd();
    } catch (e) {
      console.error('Erro ao imprimir debug do relatório:', e);
    }
  };

  const downdloadBtnStyle = {
    minWidth: "200px",
    borderColor: "#18316e",
    color: "#18316e",
    fontSize: "0.7rem",
    transition: "transform 0.3s ease-in-out",
    "&:hover": { transform: "scale(1.1)", border: "1px solid #18316e" },
  };

  const btnCalculateFilters = [
    { onClick: () => handleFiltersCalculoNew("calculo", "geral"), label: "GERAL" },
    { onClick: () => handleFiltersCalculoNew("calculo", "cprb"), label: "CPRB" },
  ];

  const btnVerbasFilters = [
    { onClick: () => handleFilterVerbas("Completo"), label: "COMPLETO" },
    { onClick: () => handleFilterVerbas("Provento"), label: "PROVENTO" },
    { onClick: () => handleFilterVerbas("Desconto"), label: "DESCONTO" },
  ];

  useEffect(() => {
    // keep resumoAtual in localStorage in sync with what is shown (dataFilter)
    try {
      localStorage.setItem("resumoAtual", JSON.stringify(dataTable.dataFilter || []));
    } catch {}
  }, [dataTable.dataFilter]);

  return (
    <>
      <TableCardv2
        columns={dataColumns}
        data={dataTable.dataFilter}
        loading={loading}
        complementaryHeaderTop={
          <>
            <div className={styles.header__filters}>
              <div className={styles.header__dateRangeFilter}>
                <GenericDatePicker
                  label="Data Inicial"
                  value={filters.datas.dtInicial}
                  onChange={(value) => handleChangeDate(value, "dtInicial")}
                />
                <GenericDatePicker
                  label="Data final"
                  value={filters.datas.dtFinal}
                  onChange={(value) => handleChangeDate(value, "dtFinal")}
                />
              </div>

              <GenericMenu
                icon={<Calculate sx={{ fontSize: "2rem", margin: "0.5px" }} />}
                buttons={btnCalculateFilters}
              />

              <ToggleButton
                color="primary"
                sx={{
                  color: "#000",
                  border: "none",
                  "&:hover": { backgroundColor: "#18316e", color: "#fff" },
                  "&.Mui-selected": { backgroundColor: "#18316e", color: "#fff" },
                }}
                value="check"
                selected={filters.calculoFilter.sefip}
                onChange={() => handleFiltersCalculoNew("sefip", true)}
              >
                sefip
              </ToggleButton>

              <GenericMenu
                icon={<RequestQuote sx={{ fontSize: "2rem", margin: "0.5px" }} />}
                buttons={btnVerbasFilters}
              />

              <Autocomplete
                sx={{ width: "300px" }}
                multiple
                options={optionsContas}
                size="small"
                value={filters.contas}
                onChange={(_, value) => handleAutocompleteChange(value)}
                disableCloseOnSelect
                renderInput={(params) => <TextField {...params} label={`Contas`} />}
              />

              <IfPermissao anyOf={CHAVES_INSS_EXPORT}>
                <Button
                  sx={downdloadBtnStyle}
                  startIcon={<FileDownloadRoundedIcon />}
                  variant="outlined"
                  color="primary"
                  size="large"
                  type="button"
                  onClick={handleExportRelatorioCompleto}
                >
                  Exportar Excel
                </Button>
              </IfPermissao>
            </div>
          </>
        }
      />
      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </>
  );
});

export default GerarRelatorioCompleto;
